import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Check, MoreHorizontal, Share2, Trash2, X } from 'lucide-react'
import { Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Card } from 'src/shared/components/ui/card'
import { Input } from 'src/shared/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useToast } from 'src/shared/hooks/use-toast'
import { DeleteQuoteDialog } from './DeleteQuoteDialog'
import { RequestQuoteAnalyticsDialog } from './RequestQuoteAnalyticsDialog'
import { useQuoteNameEditing } from '../hooks/useQuoteNameEditing'
import { useRequestContext } from '../hooks/RequestContext'
import { paths } from 'src/routes/paths'
import {
  BUILDING_QUOTE_STATUS,
  DELIVERED_STATUS,
  effectiveQuoteStatus,
} from 'src/shared/consts/quoteStatus'
import { OrderStatusSettingsMenu } from './OrderStatusSettingsMenu'

export const RequestFloatingNav = ({
  backHref,
  onBack,
  status,
  exploreMode = false,
  editableStatus = false,
  onStatusUpdated,
  endActions,
}: {
  backHref?: string
  onBack?: () => void
  status?: string | null
  exploreMode?: boolean
  editableStatus?: boolean
  onStatusUpdated?: (status: string) => void
  endActions?: ReactNode
}) => {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const { deleteQuote } = useQuoteApi({ skipListQuery: true })
  const { quote, isDemo } = useRequestContext()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const quoteName = quote?.name || t('Untitled request', 'Namnlös förfrågan')
  const quoteStatus = status ?? quote?.status
  const isDeliveredQuote = quoteStatus === DELIVERED_STATUS

  const {
    isEditing,
    editingName,
    setEditingName,
    inputRef,
    handleStartEdit,
    handleSave,
    handleCancel,
    handleKeyDown,
  } = useQuoteNameEditing({
    quoteId: quote?.id ?? null,
    quoteName,
    defaultName: t('Untitled request', 'Namnlös förfrågan'),
  })

  const requestsListHref = useMemo(() => {
    if (backHref) return backHref
    const q = searchParams.toString()
    if (q) {
      return `${paths.myAccount}${paths.requests}?${q}`
    }
    const st = quote?.status ?? status
    const stEff = effectiveQuoteStatus(st)
    if (stEff === DELIVERED_STATUS) {
      return `${paths.myAccount}${paths.requests}?segment=delivered`
    }
    if (stEff === BUILDING_QUOTE_STATUS) {
      return `${paths.myAccount}${paths.requests}?segment=drafts`
    }
    return paths.myAccount + paths.requests
  }, [backHref, searchParams, quote?.status, status])

  const shareUrl = quote?.public_id
    ? `${window.location.origin}/share-order/${quote.public_id}`
    : null

  const handleShare = async () => {
    if (!shareUrl) return

    try {
      await navigator.clipboard.writeText(shareUrl)
      toast({
        title: t('Copied!', 'Kopierat!'),
        variant: 'success',
        duration: 2000,
      })
    } catch (err) {
      console.error('Failed to copy share link:', err)
      toast({
        variant: 'destructive',
        title: t('Something went wrong', 'Något gick fel'),
        description: t('Failed to copy link', 'Kunde inte kopiera länk'),
      })
    }
  }

  const handleDeleteQuote = async () => {
    if (!quote?.id) return

    try {
      await deleteQuote.mutateAsync(quote.id)
      setDeleteDialogOpen(false)
      navigate(requestsListHref)
    } catch (error) {
      console.error('Failed to delete quote:', error)
      toast({
        variant: 'destructive',
        title: t('Something went wrong', 'Något gick fel'),
        description: t('Failed to delete request', 'Kunde inte ta bort förfrågan'),
      })
    }
  }

  return (
    <>
      <Row className="absolute top-3 left-3 z-50 max-w-[calc(100%-24px)] items-center gap-2">
        <Card className="min-w-0 gap-0 border-gray-200/80 bg-white/90 p-1.5 shadow-lg backdrop-blur-md">
          <Row className="items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => (onBack ? onBack() : navigate(requestsListHref))}
              aria-label={t('Back', 'Tillbaka')}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </Button>

            {isEditing ? (
              <Row className="min-w-0 flex-1 items-center gap-1">
                <Input
                  ref={inputRef}
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={handleSave}
                  onKeyDown={handleKeyDown}
                  className="h-7 min-w-[120px] text-sm"
                />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSave}>
                  <Check className="h-3.5 w-3.5 text-green-500" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCancel}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </Row>
            ) : (
              <h2
                className="min-w-0 flex-1 truncate text-sm font-semibold"
                style={{ cursor: isDemo ? 'default' : 'pointer' }}
                onClick={isDemo ? undefined : handleStartEdit}
              >
                {quoteName}
              </h2>
            )}

            {exploreMode ? null : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={t('More actions', 'Fler åtgärder')}
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    disabled={!shareUrl}
                    onClick={handleShare}
                  >
                    <Share2 className="mr-2 h-4 w-4" />
                    {t('Share', 'Dela')}
                  </DropdownMenuItem>

                  {!isDeliveredQuote && (
                    <DropdownMenuItem
                      className="cursor-pointer text-red-600 focus:text-red-600"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('Delete', 'Ta bort')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </Row>
        </Card>

        {endActions}

        {!isEditing && editableStatus && quote?.id != null && (
          <RequestQuoteAnalyticsDialog quoteId={quote.id} />
        )}

        {!isEditing && editableStatus && (
          <OrderStatusSettingsMenu
            status={quoteStatus}
            onUpdated={onStatusUpdated}
          />
        )}
      </Row>

      {!exploreMode ? (
        <DeleteQuoteDialog
          open={deleteDialogOpen}
          quoteName={quoteName}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteQuote}
          isLoading={deleteQuote.isPending}
        />
      ) : null}
    </>
  )
}
